import { useState, useEffect, useRef } from 'react';
import { useScrollReveal } from '@/hooks/useScrollAnimations';
import { X, ZoomIn } from 'lucide-react';

const categories = ['All', 'Monogram', 'Sublimation', 'Mugs', 'Apparel', 'Corporate', 'School Uniforms'];

const galleryItems = [
  { src: '/images/products/embroidery-towel.jpg', title: 'ABIK Embroidered Towel', category: 'Monogram' },
  { src: '/images/products/branded-apron.jpg', title: 'Leah Smartwater Branded Apron', category: 'Sublimation' },
  { src: '/images/products/custom-towel.jpg', title: 'Custom Birthday Towel', category: 'Monogram' },
  { src: '/images/products/church-stole.jpg', title: 'Church Vestment Embroidery', category: 'Monogram' },
  { src: '/images/products/prayer-mat.jpg', title: 'Custom Prayer Mat Embroidery', category: 'Monogram' },
  { src: '/images/products/custom-mug.jpg', title: 'Custom Photo Mug', category: 'Mugs' },
  { src: '/images/products/embroidery-fabric.jpg', title: 'Embroidered Fabric Design', category: 'Monogram' },
  { src: '/images/products/branded-cap.jpg', title: 'SWAGA \'23 Branded Cap', category: 'Apparel' },
  { src: '/images/products/sublimation-jersey.jpg', title: 'SWAGA \'23 Sublimation Jersey', category: 'Sublimation' },
  { src: '/images/products/branded-polo-back.jpg', title: 'Custom Branded Polo', category: 'Apparel' },
  { src: '/images/products/purple-vestment.jpg', title: 'Purple Church Vestment', category: 'Monogram' },
  { src: '/images/products/birthday-towel-blue.jpg', title: 'Birthday Embroidered Towel', category: 'Monogram' },
  { src: '/images/products/gold-vestment.jpg', title: 'Gold Liturgical Embroidery', category: 'Monogram' },
  { src: '/images/products/nurtw-polo.jpg', title: 'N.U.R.T.W Branded Polo', category: 'Corporate' },
  { src: '/images/products/memorial-jersey.jpg', title: 'Memorial Sublimation T-Shirt', category: 'Sublimation' },
  { src: '/images/products/custom-mugs-set.jpg', title: 'Custom Celebration Mugs', category: 'Mugs' },
  { src: '/images/products/custom-pillow.jpg', title: 'Custom Photo Pillow', category: 'Sublimation' },
  { src: '/images/products/printed-tshirts.jpg', title: 'Branded Organization T-Shirts', category: 'Corporate' },
  { src: '/images/products/branded-jersey.jpg', title: 'Just Imported Branded Jersey', category: 'Sublimation' },
  { src: '/images/products/bigfish-tshirt.jpg', title: 'Big Fish Custom T-Shirt', category: 'Sublimation' },
  { src: '/images/products/school-sweater.jpg', title: 'Explore Academy Sweater', category: 'School Uniforms' },
  { src: '/images/products/school-polo.jpg', title: 'Explore Academy Polo', category: 'School Uniforms' },
  { src: '/images/products/rural-service-green.jpg', title: 'Rural Service Centre Jacket', category: 'Corporate' },
  { src: '/images/products/lihoc-polo-front.jpg', title: 'LIHOC Farmers Hub Polo', category: 'Corporate' },
  { src: '/images/products/lihoc-polo-detail.jpg', title: 'LIHOC Logo Embroidery Detail', category: 'Monogram' },
  { src: '/images/products/rural-service-back.jpg', title: 'Rural Service Centre Back', category: 'Corporate' },
  { src: '/images/products/political-shirt.jpg', title: 'PDP Political Campaign Shirt', category: 'Corporate' },
  { src: '/images/products/timmac-white.jpg', title: 'Timmac Creations White', category: 'Monogram' },
  { src: '/images/products/timmac-black.jpg', title: 'Timmac Creations Black', category: 'Monogram' },
  { src: '/images/products/swaga-cap2.jpg', title: 'SWAGA \'23 Embroidered Cap', category: 'Apparel' },
  { src: '/images/products/branded-apron2.jpg', title: 'Branded Corporate Apron', category: 'Sublimation' },
  { src: '/images/products/church-stole2.jpg', title: 'Green Church Stole Embroidery', category: 'Monogram' },
  { src: '/images/products/embroidery-machine.jpg', title: 'Cross-Stitch Machine Work', category: 'Monogram' },
  { src: '/images/products/photo-pillow2.jpg', title: 'Custom Portrait Pillow', category: 'Sublimation' },
  { src: '/images/products/embroidered-cap.jpg', title: 'Embroidered Velvet Cap', category: 'Monogram' },
  { src: '/images/products/custom-labels.jpg', title: 'Custom Woven Labels', category: 'Corporate' },
  { src: '/images/products/safety-vest.jpg', title: 'Branded Safety Vest', category: 'Corporate' },
  { src: '/images/products/custom-tags.jpg', title: 'Custom Brand Tags', category: 'Corporate' },
  { src: '/images/products/university-lanyards.jpg', title: 'KWASU University Lanyards', category: 'Corporate' },
];

const Gallery = () => {
  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState<{ src: string; title: string } | null>(null);
  const { ref, isVisible } = useScrollReveal();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let animationId: number;
    const speed = 0.8;

    const scroll = () => {
      if (!isPaused && el) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth) {
          el.scrollLeft = 0;
        }
      }
      animationId = requestAnimationFrame(scroll);
    };
    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, filter]);

  // Close lightbox on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    if (lightbox) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const filtered = filter === 'All' ? galleryItems : galleryItems.filter(i => i.category === filter);

  return (
    <section id="gallery" className="py-24 bg-background">
      <div ref={ref} className="container mx-auto px-4">
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Our Work</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">Product Gallery</h2>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Browse our portfolio of custom branding, embroidery, and printing projects.</p>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === cat
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Scrolling Grid */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className={`overflow-x-auto scrollbar-hide transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div
            className="grid grid-rows-3 grid-flow-col gap-4"
            style={{ gridAutoColumns: 'minmax(200px, 1fr)' }}
          >
            {filtered.map((item, i) => (
              <div
                key={item.src + i}
                onClick={() => setLightbox({ src: item.src, title: item.title })}
                className="group relative w-48 h-48 md:w-56 md:h-56 rounded-xl overflow-hidden cursor-pointer flex-shrink-0"
                role="button"
                tabIndex={0}
                aria-label={`View ${item.title}`}
                onKeyDown={(e) => e.key === 'Enter' && setLightbox({ src: item.src, title: item.title })}
              >
                <img
                  src={item.src}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-end p-4">
                  <ZoomIn className="w-5 h-5 text-primary-foreground mb-2 opacity-70" />
                  <span className="text-primary-foreground font-semibold font-display text-sm text-center">{item.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-secondary/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button
            className="absolute top-6 right-6 text-primary-foreground bg-primary-foreground/10 rounded-full p-2 hover:bg-primary-foreground/20 transition-colors"
            onClick={() => setLightbox(null)}
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.title} className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
            <p className="text-primary-foreground/80 font-display font-medium mt-4">{lightbox.title}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
